import React, { useState } from "react";
import { Table, Modal, Button, Form, Row, Col, Badge } from "react-bootstrap";
import PaginationComponent from "../PaginationComponent";

/**
 * Modal wyświetlany przy edycji oddziału, gdy wykryto konflikty kodów pocztowych.
 * Pokazuje pełny diff: kody przenoszone z innych terminali, nowe kody, usuwane kody, ilość bez zmian.
 * Użytkownik może tylko zatwierdzić wszystkie zmiany lub anulować.
 */
const BranchEditConflictModal = ({
  show,
  onHide,
  onConfirm,
  conflictItems = [],
  removedPostcodes = [],
  unchangedCount = 0,
  submitting,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [executionDate, setExecutionDate] = useState("");
  const [rollbackDate, setRollbackDate] = useState("");

  const transferCount = conflictItems.filter((i) => i.isConflict).length;
  const newCount = conflictItems.filter((i) => !i.isConflict).length;
  const removedCount = removedPostcodes.length;

  // Budowanie pełnej listy diff do wyświetlenia w tabeli
  const allDiffItems = [
    ...conflictItems.map((item) => ({
      postcode: item.postcode,
      type: item.isConflict ? "transfer" : "new",
      currentEx: item.currentEx,
      currentDr: item.currentDr,
    })),
    ...removedPostcodes.map((postcode) => ({
      postcode,
      type: "removed",
      currentEx: null,
      currentDr: null,
    })),
  ];

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = allDiffItems.slice(startIndex, startIndex + itemsPerPage);

  const handleConfirm = () => {
    if (transferCount > 0 && !executionDate) {
      const confirmed = window.confirm(
        "Nie podano daty wykonania przeniesienia kodów.\n\n" +
        "Czy przeniesienie ma zostać wykonane natychmiastowo?\n" +
        "(Czas realizacji zależy od ilości danych do przetworzenia)"
      );
      if (!confirmed) return;
    }
    onConfirm("TransferAll", executionDate || null, rollbackDate || null);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Podsumowanie zmian kodów pocztowych</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <div className="mb-3">
          <p>Wykryto zmiany wymagające potwierdzenia:</p>
          <div className="d-flex gap-3 flex-wrap mb-2">
            {transferCount > 0 && (
              <Badge bg="warning" text="dark" className="fs-6 p-2">
                ⚠ Przeniesienie z innych terminali: {transferCount}
              </Badge>
            )}
            {newCount > 0 && (
              <Badge bg="success" className="fs-6 p-2">
                ✓ Nowe kody: {newCount}
              </Badge>
            )}
            {removedCount > 0 && (
              <Badge bg="danger" className="fs-6 p-2">
                ✕ Usuwane kody: {removedCount}
              </Badge>
            )}
            {unchangedCount > 0 && (
              <Badge bg="secondary" className="fs-6 p-2">
                Bez zmian: {unchangedCount}
              </Badge>
            )}
          </div>
        </div>

        <Table responsive striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Kod pocztowy</th>
              <th>Zmiana</th>
              <th>Obecny terminal EX</th>
              <th>Obecny terminal DR</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr
                  key={index}
                  className={
                    item.type === "transfer"
                      ? "table-warning"
                      : item.type === "new"
                      ? "table-success"
                      : "table-danger"
                  }
                >
                  <td>{item.postcode}</td>
                  <td>
                    {item.type === "transfer" ? (
                      <span className="text-danger fw-bold">
                        ⚠ Przeniesienie z {item.currentEx}/{item.currentDr}
                      </span>
                    ) : item.type === "new" ? (
                      <span className="text-success">✓ Nowy kod</span>
                    ) : (
                      <span className="text-danger">✕ Usunięcie</span>
                    )}
                  </td>
                  <td>{item.currentEx || "—"}</td>
                  <td>{item.currentDr || "—"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center">
                  Brak danych
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        {allDiffItems.length > itemsPerPage && (
          <PaginationComponent
            itemsPerPage={itemsPerPage}
            dataCount={paginatedData.length}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(pageSize) => {
              setItemsPerPage(pageSize);
              setCurrentPage(1);
            }}
            itemsPerPageOptions={[100, 250, 500, 1000, 5000]}
          />
        )}

        {transferCount > 0 && (
          <>
            <hr />
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="editConflictExecutionDate">
                  <Form.Label>Data wykonania przeniesienia (opcjonalna)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={executionDate}
                    onChange={(e) => setExecutionDate(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Brak daty oznacza natychmiastowe wykonanie.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="editConflictRollbackDate">
                  <Form.Label>Data wycofania (opcjonalna)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={rollbackDate}
                    onChange={(e) => setRollbackDate(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" onClick={onHide}>
          Anuluj
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? "Zapisywanie..." : "Zatwierdź zmiany"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BranchEditConflictModal;
